import type { NetworkDoc, NetworkLink, NetworkNode, NetworkSegment } from '../../../types'
import type { IpRow, LiveDevice } from './netModel'
import { KINDS, MODES, SERIALS, freeIps, kindInfo, lampOf, newSegment, parseCidr } from './netModel'
import { Lamp } from '../../../components/Lamp'

// 도면 오른쪽 패널 — 고른 노드/연결의 속성, 아무것도 안 골랐으면 IP 대역 + IP 표

const input = 'w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-surface'
const label = 'block text-[11px] text-gray-500 mb-0.5'

type Update = (update: (d: NetworkDoc) => NetworkDoc) => void

const CONVERTER_MEMO = `포트1: RS-485 2선 · 9600 8N1 · 4001
연결 설비: 1~3번 온도조절기 (국번 1~3)`

export function NodeForm({ node, comms, onChange, onDelete }: {
  node: NetworkNode
  comms: LiveDevice[]
  onChange: (patch: Partial<NetworkNode>) => void
  onDelete: (() => void) | null
}) {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800">{kindInfo(node.kind).label}</h4>
        {onDelete && <button onClick={onDelete} className="ml-auto text-xs text-red-600 hover:underline">삭제</button>}
      </div>
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className={label}>이름</label>
              <input className={input} value={node.name} onChange={e => onChange({ name: e.target.value })} placeholder={kindInfo(node.kind).label} />
            </div>
            {node.kind !== 'edge' && (
              <div className="col-span-2">
                <label className={label}>종류</label>
                <select className={input} value={node.kind} onChange={e => onChange({ kind: e.target.value as NetworkNode['kind'] })}>
                  {KINDS.filter(k => k.kind !== 'edge' && k.kind !== 'device' && (k.kind !== 'other' || node.kind === 'other')).map(k => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={label}>IP</label>
              <input className={`${input} font-mono`} value={node.ip} onChange={e => onChange({ ip: e.target.value })} placeholder="192.168.0.10" />
            </div>
            <div>
              <label className={label}>MAC</label>
              <input className={`${input} font-mono`} value={node.mac} onChange={e => onChange({ mac: e.target.value })} />
            </div>
            <div>
              <label className={label}>모델</label>
              <input className={input} value={node.model} onChange={e => onChange({ model: e.target.value })} placeholder={node.kind === 'converter' ? 'Moxa NPort 5150' : ''} />
            </div>
            {(node.kind === 'switch' || node.kind === 'hub') ? (
              <div>
                <label className={label}>포트 수</label>
                <input className={input} type="number" min={0} max={512} value={node.ports || ''} onChange={e => onChange({ ports: Math.max(0, Math.min(512, Number(e.target.value) || 0)) })} />
              </div>
            ) : <div />}
            {node.kind === 'converter' && (
              <>
                <div>
                  <label className={label}>시리얼 쪽</label>
                  <select className={input} value={node.serial} onChange={e => onChange({ serial: e.target.value as NetworkNode['serial'] })}>
                    <option value="">미지정</option>
                    {SERIALS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>네트워크 쪽</label>
                  <select className={input} value={node.mode} onChange={e => onChange({ mode: e.target.value as NetworkNode['mode'] })}>
                    <option value="">미지정</option>
                    {MODES.map(m => <option key={m.mode} value={m.mode}>{m.label}</option>)}
                  </select>
                </div>
                {MODES.find(m => m.mode === node.mode)?.hint && (
                  <p className="col-span-2 -mt-1 text-[11px] text-gray-400">{MODES.find(m => m.mode === node.mode)!.hint}</p>
                )}
              </>
            )}
            <div className="col-span-2">
              <label className={label}>설치 위치</label>
              <input className={input} value={node.location} onChange={e => onChange({ location: e.target.value })} placeholder="2층 배전반 등" />
            </div>
          </div>
        </>
        <div>
          <div className={label}>통신 디바이스</div>
          {comms.length ? (
            <ul className="rounded-md border border-gray-100 divide-y divide-gray-50">
              {comms.map(d => (
                <li key={d.code} className="flex items-center gap-1.5 px-2 py-1 text-xs">
                  <Lamp state={lampOf(d)} className="shrink-0" />
                  <span className="font-mono text-gray-700">{d.code}</span>
                  <span className="text-gray-500 truncate">{d.name}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-gray-400">{d.protocol}{d.port ? ` · ${d.port}` : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-gray-400">
              {node.ip ? '없음' : 'IP를 입력하면 표시됩니다.'}
            </p>
          )}
        </div>
      <div>
        <label className={label}>{node.kind === 'converter' ? '연결 설정' : '메모'}</label>
        <textarea className={`${input} ${node.kind === 'converter' ? 'min-h-[120px]' : 'min-h-[64px]'}`} value={node.memo}
          onChange={e => onChange({ memo: e.target.value })}
          placeholder={node.kind === 'converter' ? CONVERTER_MEMO : ''} />
      </div>
    </div>
  )
}

export function LinkForm({ link, from, to, onChange, onDelete }: {
  link: NetworkLink
  from: string
  to: string
  onChange: (patch: Partial<NetworkLink>) => void
  onDelete: () => void
}) {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800">연결</h4>
        <button onClick={onDelete} className="ml-auto text-xs text-red-600 hover:underline">삭제</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={label}>{from} 포트</label>
          <input className={input} value={link.fromPort} onChange={e => onChange({ fromPort: e.target.value })} placeholder="1" />
        </div>
        <div>
          <label className={label}>{to} 포트</label>
          <input className={input} value={link.toPort} onChange={e => onChange({ toPort: e.target.value })} placeholder="24" />
        </div>
        <div className="col-span-2">
          <label className={label}>케이블·매체</label>
          <input className={input} list="net-link-labels" value={link.label} onChange={e => onChange({ label: e.target.value })} placeholder="UTP, RS-485, 광 …" />
          <datalist id="net-link-labels">
            {['UTP', 'RS-485', 'RS-232', '광', '무선'].map(v => <option key={v} value={v} />)}
          </datalist>
        </div>
      </div>
    </div>
  )
}

export function MultiForm({ nodes, links, locked, onDelete, onClear }: {
  nodes: string[]
  links: number
  locked: boolean
  onDelete: () => void
  onClear: () => void
}) {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800">
          {[nodes.length && `노드 ${nodes.length}개`, links && `연결 ${links}개`].filter(Boolean).join(' · ')} 선택
        </h4>
        <button onClick={onClear} className="ml-auto text-xs text-gray-500 hover:underline">선택 해제</button>
      </div>
      {nodes.length > 0 && <div className="text-[11px] text-gray-500 leading-relaxed">{nodes.join(', ')}</div>}
      <p className="text-[11px] text-gray-400">Shift·Ctrl+클릭: 추가/제외</p>
      <button onClick={onDelete} className="border border-red-300 text-red-600 rounded-md px-3 py-1.5 text-xs hover:bg-red-50">
        선택 삭제{locked ? ' (Edge 서버 제외)' : ''}
      </button>
    </div>
  )
}

export function Overview({ doc, rows, orphans, onUpdate, onAddAt, onFocus }: {
  doc: NetworkDoc
  rows: IpRow[]
  orphans: { ips: { ip: string; devices: LiveDevice[] }[]; noIp: LiveDevice[] }
  onUpdate: Update
  onAddAt: (kind: NetworkNode['kind'], ip: string) => void
  onFocus: (nodeKey: string) => void
}) {
  const setSegment = (key: string, patch: Partial<NetworkSegment>) =>
    onUpdate(d => ({ ...d, segments: d.segments.map(s => (s.key === key ? { ...s, ...patch } : s)) }))
  const used = new Set(rows.map(r => r.ip))
  const issues = rows.filter(r => r.issue).length
  return (
    <div className="divide-y divide-gray-100">
      {orphans.ips.length > 0 && (
        <div className="p-4 bg-amber-50/50 space-y-2">
          <div className="text-xs text-gray-700">디바이스가 통신하는데 도면에 없는 IP {orphans.ips.length}개</div>
          {orphans.ips.map(o => (
            <div key={o.ip} className="rounded-md bg-surface border border-gray-200 px-2 py-1.5">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-800">{o.ip}</span>
                <span className="ml-auto flex gap-1">
                  <button onClick={() => onAddAt('converter', o.ip)} className="text-[11px] text-accent hover:underline">+ 변환기</button>
                  <button onClick={() => onAddAt('equipment', o.ip)} className="text-[11px] text-accent hover:underline">+ 설비</button>
                </span>
              </div>
              <div className="mt-0.5 text-[11px] text-gray-500 truncate">
                {o.devices.map(d => `${d.code}${d.port ? `:${d.port}` : ''}`).join(', ')} · {o.devices[0].protocol}
              </div>
            </div>
          ))}
        </div>
      )}
      {orphans.noIp.length > 0 && (
        <div className="px-4 py-2 text-[11px] text-gray-400">
          IP 없는 디바이스: {orphans.noIp.map(d => d.code).join(', ')}
        </div>
      )}

      <div className="p-4 space-y-2">
        <div className="flex items-center">
          <h4 className="text-sm font-semibold text-gray-800">IP 대역</h4>
          <button onClick={() => onUpdate(d => ({ ...d, segments: [...d.segments, newSegment()] }))}
            className="ml-auto text-xs text-accent hover:underline">+ 대역</button>
        </div>
        {doc.segments.length === 0 && <p className="text-[11px] text-gray-400">대역을 추가하면 대역 밖 IP와 빈 IP를 표시합니다.</p>}
        {doc.segments.map(s => {
          const valid = !s.cidr || parseCidr(s.cidr)
          const free = freeIps(s, used, 3)
          return (
            <div key={s.key} className="rounded-md border border-gray-200 p-2 space-y-1.5">
              <div className="flex gap-1.5">
                <input className={`${input} flex-1`} value={s.name} onChange={e => setSegment(s.key, { name: e.target.value })} placeholder="이름" />
                <button title="대역 삭제" onClick={() => onUpdate(d => ({ ...d, segments: d.segments.filter(x => x.key !== s.key) }))}
                  className="px-1.5 text-gray-400 hover:text-red-600">×</button>
              </div>
              <div className="grid grid-cols-[1fr_64px] gap-1.5">
                <input className={`${input} font-mono ${valid ? '' : 'border-red-400'}`} value={s.cidr} onChange={e => setSegment(s.key, { cidr: e.target.value })} placeholder="192.168.30.0/24" />
                <input className={input} value={s.vlan} onChange={e => setSegment(s.key, { vlan: e.target.value })} placeholder="VLAN" />
              </div>
              <input className={`${input} font-mono`} value={s.gateway} onChange={e => setSegment(s.key, { gateway: e.target.value })} placeholder="게이트웨이 192.168.30.1" />
              {free.length > 0 && <div className="text-[11px] text-gray-400">빈 IP: <span className="font-mono">{free.join(', ')}</span> …</div>}
            </div>
          )
        })}
      </div>

      <div className="p-4">
        <div className="flex items-center mb-2">
          <h4 className="text-sm font-semibold text-gray-800">IP 표</h4>
          <span className={`ml-auto text-[11px] ${issues ? 'text-red-600' : 'text-gray-400'}`}>{rows.length}개{issues ? ` · 문제 ${issues}` : ''}</span>
        </div>
        {rows.length === 0 ? <p className="text-[11px] text-gray-400">IP가 등록된 장비가 없습니다.</p> : (
          <table className="w-full text-[11px]">
            <tbody className="divide-y divide-gray-50">
              {rows.map(r => (
                <tr key={r.ip} className="align-top">
                  <td className={`py-1 pr-2 font-mono whitespace-nowrap ${r.issue ? 'text-red-600' : 'text-gray-700'}`}>{r.ip}</td>
                  <td className="py-1">
                    {r.nodes.map(n => (
                      <button key={n.key} onClick={() => onFocus(n.key)} className="block text-left text-gray-700 hover:text-accent truncate max-w-[150px]">
                        {n.name || n.deviceCode || kindInfo(n.kind).label}
                      </button>
                    ))}
                    {r.devices.length > 0 && <div className="text-gray-400 font-mono truncate max-w-[150px]">통신 {r.devices.map(d => d.code).join(', ')}</div>}
                    {r.issue && <div className="text-red-600">{r.issue}</div>}
                  </td>
                  <td className="py-1 pl-2 text-gray-400 whitespace-nowrap">{r.segment?.name || r.segment?.cidr || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
